import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Printer, CheckCircle2, ShieldCheck, RotateCcw, Save } from 'lucide-react';
import { care, fail, can, openFile } from './careApi';
import { Badge, Btn, Input, Loading } from './careUi';
import SignaturePad from './SignaturePad';

const partyText = (t, co) => (t.startsWith('(1) Sdn. Bhd.') && co.legal_name
  ? `(1) ${co.legal_name} (Company No.: ${co.provider_company_no || co.registration_no}), a company incorporated in Malaysia with its registered office at ${co.provider_registered_office || co.address} (the “Provider”) on one part;`
  : t);

export const SigBlock = ({ def, v = {}, set, locked, ft }) => (
  <div className="border border-slate-200 rounded-xl p-4" data-testid={`sig-${ft}-${def.key}`}>
    <p className="text-xs font-bold tracking-wide text-slate-700 mb-2">{def.label}</p>
    <SignaturePad value={v.image} disabled={locked} onChange={(img) => set({ ...v, image: img })} testid={`sig-pad-${ft}-${def.key}`} />
    <div className="grid grid-cols-2 gap-2 mt-3">
      {def.fields.map((f) => (
        <Input key={f} label={{ name: 'Name', ic: 'I/C No.', date: 'Date' }[f]} type={f === 'date' ? 'date' : 'text'} disabled={locked}
          value={v[f] || ''} onChange={(e) => set({ ...v, [f]: e.target.value })} testid={`sig-${ft}-${def.key}-${f}`} className={f === 'name' ? 'col-span-2' : ''} />
      ))}
    </div>
    {v.signed_at && <p className="text-[10px] text-slate-400 mt-2">E-signed {v.signed_at.slice(0, 19).replace('T', ' ')} UTC · recorded by {v.recorded_by}</p>}
  </div>
);

export const Admission = ({ tpl, data, setData, locked, lockOffice }) => (
  <div className="space-y-5">
    {tpl.sections.map((s) => (
      <section key={s.key}>
        <div className="bg-[#181B30] text-white text-xs font-bold tracking-wide px-3 py-1.5 rounded">{s.title}</div>
        {lockOffice && s.key === 'office' && <p className="text-[11px] text-slate-500 mt-2" data-testid="office-locked-hint">Completed by the care home staff.</p>}
        <div className={`grid gap-3 mt-3 ${s.textarea ? '' : 'sm:grid-cols-2'}`}>
          {s.fields.map(([k, label]) => (
            <Input key={k} label={label} textarea={s.textarea} disabled={locked || (lockOffice && s.key === 'office')} value={(data[s.key] || {})[k] || ''} testid={`f-${s.key}-${k}`}
              onChange={(e) => setData({ ...data, [s.key]: { ...(data[s.key] || {}), [k]: e.target.value } })} />
          ))}
        </div>
      </section>
    ))}
    {tpl.notes.map((n) => <p key={n} className="text-sm text-slate-700">{n}</p>)}
  </div>
);

export const Pdpa = ({ tpl }) => (
  <div className="space-y-2">
    <p className="text-center font-bold text-slate-900 text-sm">{tpl.title}<br /><span className="text-slate-600">{tpl.title_bm}</span></p>
    {tpl.paragraphs.map((p, i) => (
      <p key={i} className={`text-[13px] leading-relaxed ${p.lang === 'bm' ? 'italic text-slate-500 mb-3' : 'text-slate-800'}`}>{p.text}</p>
    ))}
  </div>
);

export const Contract = ({ tpl, data, setData, locked, co }) => (
  <div className="space-y-2">
    {tpl.paragraphs.map((p, i) => {
      if (p.kind === 'heading') return <h4 key={i} className="font-bold text-slate-900 text-sm pt-3">{p.text}</h4>;
      if (p.kind === 'field') {
        const label = p.text.split(':')[0] + ':';
        const k = tpl.field_keys[label];
        return <Input key={i} label={label} disabled={locked} value={data[k] || ''} testid={`f-contract-${k}`} onChange={(e) => setData({ ...data, [k]: e.target.value })} />;
      }
      return <p key={i} className={`text-[13px] leading-relaxed text-slate-800 ${/^(\d\)|\(i|\d\.\s)/.test(p.text) ? 'pl-6' : ''}`}>{partyText(p.text, co)}</p>;
    })}
    <h4 className="font-bold text-slate-900 text-sm pt-4">SIGNATURES</h4>
    <p className="text-sm font-semibold">{tpl.acceptance}</p>
  </div>
);

export const Checklist = ({ tpl, data, setData, locked }) => {
  const checked = new Set(data.checked || []);
  const toggle = (it) => { const n = new Set(checked); n.has(it) ? n.delete(it) : n.add(it); setData({ ...data, checked: [...n] }); };
  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">{tpl.intro}</p>
      {tpl.groups.map((g) => (
        <div key={g.title}>
          <p className="text-xs font-bold text-slate-800 mb-2">{g.title}</p>
          {g.items.map((it, i) => (
            <label key={it} className="flex items-start gap-2.5 py-1 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" className="mt-0.5 accent-teal-700 w-4 h-4" disabled={locked} checked={checked.has(it)} onChange={() => toggle(it)}
                data-testid={`chk-${g.title[0]}-${i}`} />{it}
            </label>
          ))}
        </div>
      ))}
    </div>
  );
};

export default function FormEditor({ rid, ft, tpl, company, label, onChanged }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => care.get(`/residents/${rid}/forms/${ft}`).then((r) => setF(r.data)).catch(fail);
  useEffect(() => { setF(null); load(); }, [rid, ft]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return <Loading />;
  const locked = f.status === 'Approved';
  const setData = (data) => setF({ ...f, data });
  const setSig = (k, v) => setF({ ...f, signatures: { ...f.signatures, [k]: v } });

  const act = async (kind) => {
    setBusy(kind);
    try {
      const body = { data: f.data, signatures: f.signatures };
      const { data } = kind === 'save' ? await care.put(`/residents/${rid}/forms/${ft}`, body)
        : kind === 'complete' ? await care.post(`/residents/${rid}/forms/${ft}/complete`, body)
          : kind === 'approve' ? await care.post(`/residents/${rid}/forms/${ft}/approve`)
            : await care.post(`/residents/${rid}/forms/${ft}/reopen`, { note: 'Reopened for correction' });
      setF(data);
      toast.success({ save: 'Draft saved', complete: 'Form completed and signed PDF stored', approve: 'Form approved', reopen: 'Form reopened' }[kind]);
      onChanged && onChanged();
    } catch (e) { fail(e); } finally { setBusy(''); }
  };

  return (
    <div data-testid={`form-editor-${ft}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><h3 className="font-semibold text-slate-900">{label}</h3><Badge s={f.status} testid={`form-status-${ft}`} /></div>
        <div className="flex flex-wrap gap-2">
          <Btn variant="ghost" onClick={() => openFile(`/residents/${rid}/forms/${ft}/pdf`)} data-testid={`form-print-${ft}`}><Printer className="w-4 h-4" />View / Print</Btn>
          <Btn variant="ghost" onClick={() => openFile(`/residents/${rid}/forms/${ft}/pdf`, true, `${ft}.pdf`)} data-testid={`form-download-${ft}`}><Download className="w-4 h-4" />PDF</Btn>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-8 max-w-4xl">
        <div className="text-center mb-6">
          <p className="text-2xl font-extrabold tracking-wide text-[#181B30]">{company.company_name}</p>
          {ft !== 'pdpa' && <p className="font-bold text-slate-800 mt-1">{tpl.title}</p>}
          <p className="text-xs text-slate-500 mt-1">{company.address}</p>
        </div>
        {ft === 'admission' && <Admission tpl={tpl} data={f.data || {}} setData={setData} locked={locked} />}
        {ft === 'pdpa' && <Pdpa tpl={tpl} />}
        {ft === 'contract' && <Contract tpl={tpl} data={f.data || {}} setData={setData} locked={locked} co={company} />}
        {ft === 'checklist' && <Checklist tpl={tpl} data={f.data || {}} setData={setData} locked={locked} />}
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {tpl.signatures.map((d) => <SigBlock key={d.key} def={d} ft={ft} v={(f.signatures || {})[d.key]} set={(v) => setSig(d.key, v)} locked={locked} />)}
        </div>
        {ft === 'pdpa' && <p className="text-[11px] text-slate-400 mt-3">{tpl.version}</p>}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {!locked && <Btn variant="ghost" loading={busy === 'save'} onClick={() => act('save')} data-testid={`form-save-${ft}`}><Save className="w-4 h-4" />Save draft</Btn>}
        {!locked && <Btn loading={busy === 'complete'} onClick={() => act('complete')} data-testid={`form-complete-${ft}`}><CheckCircle2 className="w-4 h-4" />Mark completed</Btn>}
        {can('admin') && f.status === 'Completed' && <Btn variant="dark" loading={busy === 'approve'} onClick={() => act('approve')} data-testid={`form-approve-${ft}`}><ShieldCheck className="w-4 h-4" />Approve</Btn>}
        {can('admin') && locked && <Btn variant="ghost" loading={busy === 'reopen'} onClick={() => act('reopen')} data-testid={`form-reopen-${ft}`}><RotateCcw className="w-4 h-4" />Reopen</Btn>}
      </div>
      {f.history?.length > 0 && (
        <div className="mt-6 max-w-4xl" data-testid={`form-history-${ft}`}>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Audit trail</p>
          <ul className="text-xs text-slate-600 space-y-1">
            {f.history.map((h) => <li key={h.id}>{h.at.slice(0, 16).replace('T', ' ')} · {h.user_name} · {h.action.replace(/_/g, ' ')}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
